import { Form, Input, Select, Button, Card, Tooltip } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { getCategories } from '@/api/categories';
import { getPanels } from '@/api/panels';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getCurrentDeveloperId } from '@/stores/authStore';
import type { 
  CreateProductRequest, 
  UpdateProductRequest,
  ProductCategory,
  PanelTemplate 
} from '@/types';
// 导入配套图标
import { SaveOutlined, PlusCircleOutlined, AppleOutlined, AndroidOutlined } from '@ant-design/icons';

/**
 * 提取所有 Level 3 的类别
 */
const extractLevel3Categories = (categories: ProductCategory[]): ProductCategory[] => {
  const level3Categories: ProductCategory[] = [];
  
  const traverse = (cats: ProductCategory[]) => {
    cats.forEach(cat => {
      if (cat.level === 3) {
        level3Categories.push(cat);
      } else if (cat.children) {
        traverse(cat.children);
      }
    });
  };
  
  traverse(categories);
  return level3Categories;
};

/**
 * 表单值类型定义
 */
interface FormValues {
  name: string;
  category_id?: string;
  ios_panel_id?: string;
  android_panel_id?: string;
}

/**
 * 产品表单组件（创建/编辑）
 */
export const ProductForm = ({
  mode = 'create',
  initialValues,
  onSubmit,
}: {
  mode?: 'create' | 'edit';
  initialValues?: Partial<CreateProductRequest & UpdateProductRequest>;
  onSubmit: (values: CreateProductRequest | UpdateProductRequest) => Promise<void>;
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const developerId = getCurrentDeveloperId();

  // 获取类别列表
  const categoriesQuery = useQuery({
    queryKey: ['categories', developerId, 3],
    queryFn: () => getCategories(3),
    enabled: !!developerId,
  });

  // 获取面板列表
  const panelsQuery = useQuery({
    queryKey: ['panels', developerId],
    queryFn: getPanels,
    enabled: !!developerId,
  });

  const categories = categoriesQuery.data;
  const panels = panelsQuery.data;
  
  const loadingCategories = categoriesQuery.isLoading;
  const loadingPanels = panelsQuery.isLoading;

  // 安全的面板处理
  const safePanels = Array.isArray(panels) ? panels : [];
  const iosPanels = safePanels.filter(p => p.file_type === 'jsbundle');
  const androidPanels = safePanels.filter(p => p.file_type === 'bundle');

  // 提取 Level 3 类别
  const level3Categories = categories ? extractLevel3Categories(categories) : [];

  /**
   * 处理表单提交
   */
  const onFinish = async (values: FormValues): Promise<void> => {
    setSubmitting(true);
    setError(null);

    try {
      if (mode === 'create') {
        // 创建产品时需要类别ID
        if (!values.category_id) {
          throw new Error('请选择产品类别');
        }
        
        // 构建创建数据
        const submitData: CreateProductRequest = {
          name: values.name,
          category_id: values.category_id,
          ios_panel_id: values.ios_panel_id || undefined,
          android_panel_id: values.android_panel_id || undefined,
        };
        
        await onSubmit(submitData);
      } else {
        // 更新产品时保持原有类别ID，不更新类别
        const submitData: UpdateProductRequest = {
          name: values.name,
          ios_panel_id: values.ios_panel_id || undefined,
          android_panel_id: values.android_panel_id || undefined,
        };
        
        await onSubmit(submitData);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '操作失败，请检查参数后重试。';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // 加载状态
  if (loadingCategories || loadingPanels) {
    return <LoadingSpinner />;
  }

  // 根据模式设置初始值
  const formInitialValues = {
    name: initialValues?.name,
    category_id: mode === 'create' ? initialValues?.category_id : undefined,
    ios_panel_id: initialValues?.ios_panel_id,
    android_panel_id: initialValues?.android_panel_id,
  };

  return (
    <div className="w-full">
      {error && (
        <ErrorAlert 
          message="操作失败" 
          description={error} 
          className="mb-6"
          showIcon
        />
      )}

      <Form
        form={form}
        name="product"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        initialValues={formInitialValues}
        className="space-y-6"
      >
        <Form.Item
          name="name"
          label="产品名称"
          tooltip="请输入唯一的产品名称，如「智能灯带」「温湿度传感器」。"
          rules={[{ required: true, message: '请输入产品名称' }, { max: 50, message: '产品名称不能超过50个字符' }]}
        >
          <Input 
            placeholder="例如：智能灯带、温湿度传感器" 
            size="large"
            disabled={submitting}
            style={{ borderRadius: '8px' } as React.CSSProperties}
          />
        </Form.Item>

        {/* 创建模式下显示类别选择 */}
        {mode === 'create' && (
          <Form.Item
            name="category_id"
            label="产品类别"
            tooltip="请选择产品类别，用于产品分类管理。"
            rules={[{ required: true, message: '请选择产品类别' }]}
          >
            <Select
              placeholder="请选择产品类别"
              size="large"
              disabled={submitting || level3Categories.length === 0}
              options={level3Categories.map(cat => ({
                value: cat.id,
                label: cat.name,
              }))}
              showSearch
              optionFilterProp="label"
              style={{ borderRadius: '8px' } as React.CSSProperties}
              notFoundContent={level3Categories.length === 0 ? '暂无类别数据，请先创建类别。' : '未找到匹配项'}
            />
          </Form.Item>
        )}

        {/* 编辑模式下显示类别信息但不可更改 */}
        {mode === 'edit' && categories && initialValues?.category_id && (
          <Form.Item
            label="产品类别"
            className="mb-8"
          >
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-gray-700">
              {extractLevel3Categories(categories)
                .find(cat => cat.id === initialValues.category_id)?.name || 
                '未知类别'}
              <p className="text-xs text-gray-500 mt-1">产品类别创建后不可修改</p>
            </div>
          </Form.Item>
        )}

        {/* 面板关联卡片 - 优化样式 */}
        <Card 
          title={
            <div className="flex items-center gap-2">
              <PlusCircleOutlined className="text-blue-500" />
              面板关联配置
            </div>
          }
          className="border-0 shadow-sm"
          style={{ 
            '--ant-card-head-font-size': '16px',
            '--ant-card-head-font-weight': 600,
            borderRadius: '8px'
          } as React.CSSProperties}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Form.Item
              name="ios_panel_id"
              label={
                <Tooltip title="选择iOS端对应的jsbundle面板文件">
                  <div className="flex items-center gap-1">
                    <AppleOutlined /> iOS 面板
                  </div>
                </Tooltip>
              }
            >
              <Select
                placeholder="选择 iOS 面板 (.jsbundle)"
                size="large"
                disabled={submitting || iosPanels.length === 0}
                options={iosPanels.map((panel: PanelTemplate) => ({
                  value: panel.id,
                  label: `${panel.name} v${panel.version}`,
                }))}
                allowClear
                style={{ borderRadius: '8px' } as React.CSSProperties}
                notFoundContent={iosPanels.length === 0 ? '暂无iOS面板数据' : '未找到匹配项'}
              />
            </Form.Item>

            <Form.Item
              name="android_panel_id"
              label={
                <Tooltip title="选择Android端对应的bundle面板文件">
                  <div className="flex items-center gap-1">
                    <AndroidOutlined /> Android 面板
                  </div>
                </Tooltip>
              }
            >
              <Select
                placeholder="选择 Android 面板 (.bundle)"
                size="large"
                disabled={submitting || androidPanels.length === 0}
                options={androidPanels.map((panel: PanelTemplate) => ({
                  value: panel.id,
                  label: `${panel.name} v${panel.version}`,
                }))}
                allowClear
                style={{ borderRadius: '8px' } as React.CSSProperties}
                notFoundContent={androidPanels.length === 0 ? '暂无Android面板数据' : '未找到匹配项'}
              />
            </Form.Item>
          </div>
          <p className="text-xs text-gray-500 mt-2">提示：面板为可选配置，未关联面板的产品将无法在客户端展示控制界面。</p>
        </Card>

        <br />

        {/* 提交按钮 - 优化样式 */}
        <Form.Item className="mb-0">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitting}
            disabled={submitting}
            icon={<SaveOutlined />}
            className="w-full py-2 hover:shadow-md transition-shadow"
            style={{ borderRadius: '8px', fontSize: '16px' } as React.CSSProperties}
          >
            {submitting ? '处理中...' : mode === 'create' ? '创建产品' : '更新产品'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};